import { useState, useEffect, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, LogOut, ScanBarcode } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Avatar } from '../data-display/Avatar';
import { Button } from '../Button';

export interface SidebarNavSubItem {
  /** Route path (e.g. '/stock/actuel') */
  to: string;
  label: string;
  /** Optional icon — falls back to a default bullet icon */
  icon?: ReactNode;
}

export type SidebarNavItem =
  | {
      key: string;
      label: string;
      icon: ReactNode;
      to: string;
      children?: never;
    }
  | {
      key: string;
      label: string;
      icon: ReactNode;
      to?: never;
      children: SidebarNavSubItem[];
    };

export interface SidebarUser {
  firstName: string;
  lastName: string;
  email: string;
  /** Optional role label displayed under the email */
  role?: string;
}

export interface SidebarProps {
  /** Logo rendered in the top-left of the header. Pass an <img> or any ReactNode. */
  logo: ReactNode;
  /**
   * Already-filtered and ordered navigation items.
   * The consumer is responsible for permission filtering, brand-scoping, ordering, etc.
   */
  navItems: SidebarNavItem[];
  /** Optional cross-app switcher rendered next to the logo (e.g. AppSwitcher) */
  appSwitcher?: ReactNode;
  /** Optional action(s) rendered to the right of the logo (e.g. notification bell) */
  headerActions?: ReactNode;
  /** Optional brand switcher rendered below the header */
  brandSwitcher?: ReactNode;
  /** Authenticated user shown in the footer */
  user?: SidebarUser;
  /** Called when the user clicks "Déconnexion" */
  onLogout: () => void;
  /** Optional extras rendered in the footer (e.g. a "What's new" badge) */
  footerExtras?: ReactNode;
  /** Optional drawer / overlay rendered as a sibling (portals, notification drawer, etc.) */
  children?: ReactNode;
  /** Logout button label (defaults to "Déconnexion") */
  logoutLabel?: string;
  /**
   * Extra classes merged on the `<aside>` (via `cn`). Default size stays
   * `h-screen w-60`; pass e.g. `h-full` when a parent (AppShell) sets the height.
   */
  className?: string;
}

/**
 * Generic sidebar layout — used by BStock, BStaff, and future product frontends.
 *
 * The component is purely presentational: it doesn't know anything about permissions,
 * brand scoping, or app-specific features. The consumer passes already-filtered
 * navItems and composes the slots (header, brand switcher, footer extras) itself.
 */
export function Sidebar({
  logo,
  navItems,
  appSwitcher,
  headerActions,
  brandSwitcher,
  user,
  onLogout,
  footerExtras,
  children,
  logoutLabel = 'Deconnexion',
  className,
}: SidebarProps) {
  const location = useLocation();
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const isGroupActive = (items: SidebarNavSubItem[]) =>
    items.some((child) => location.pathname.startsWith(child.to));

  // Auto-expand groups when a child becomes active
  useEffect(() => {
    navItems.forEach((item) => {
      if (item.children && isGroupActive(item.children)) {
        setExpandedGroups((prev) => new Set(prev).add(item.key));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, navItems]);

  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  return (
    <aside className={cn('flex h-screen w-60 flex-col bg-sidebar-bg text-white', className)}>
      {/* Fixed header */}
      <div className="flex-shrink-0 border-b border-sidebar-border">
        <div className="flex h-16 items-center justify-between px-6">
          <div className="flex items-center gap-2">
            {logo}
            {appSwitcher}
          </div>
          {headerActions}
        </div>
        {brandSwitcher}
      </div>

      {/* Scrollable navigation */}
      <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-4 space-y-1">
        {navItems.map((item) => (
          <div key={item.key}>
            {item.children ? (
              <>
                <button
                  type="button"
                  onClick={() => toggleGroup(item.key)}
                  className={cn(
                    'group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-150',
                    isGroupActive(item.children)
                      ? 'bg-sidebar-hover text-white'
                      : 'text-sidebar-text hover:bg-sidebar-hover hover:text-white'
                  )}
                >
                  <span
                    className={cn(
                      'transition-colors duration-150',
                      isGroupActive(item.children)
                        ? 'text-white'
                        : 'text-sidebar-icon group-hover:text-white'
                    )}
                  >
                    {item.icon}
                  </span>
                  <span className="flex-1 text-left">{item.label}</span>
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 transition-transform duration-150',
                      expandedGroups.has(item.key) ? 'rotate-180' : '',
                      isGroupActive(item.children)
                        ? 'text-white'
                        : 'text-sidebar-chevron group-hover:text-white'
                    )}
                  />
                </button>

                {expandedGroups.has(item.key) && (
                  <div className="mt-1 ml-4 pl-4 border-l border-sidebar-border space-y-1">
                    {item.children.map((child) => (
                      <NavLink
                        key={child.to}
                        to={child.to}
                        className={({ isActive }) =>
                          cn(
                            'group flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-all duration-150',
                            isActive
                              ? 'text-white bg-sidebar-hover'
                              : 'text-sidebar-text hover:text-white hover:bg-sidebar-hover'
                          )
                        }
                      >
                        <span
                          className={cn(
                            'transition-colors duration-150',
                            location.pathname === child.to
                              ? 'text-white'
                              : 'text-sidebar-icon group-hover:text-white'
                          )}
                        >
                          {child.icon ?? <ScanBarcode className="h-4 w-4" />}
                        </span>
                        {child.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <NavLink
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  cn(
                    'group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-150',
                    isActive
                      ? 'bg-sidebar-hover text-white'
                      : 'text-sidebar-text hover:bg-sidebar-hover hover:text-white'
                  )
                }
              >
                <span
                  className={cn(
                    'transition-colors duration-150',
                    location.pathname === item.to
                      ? 'text-white'
                      : 'text-sidebar-icon group-hover:text-white'
                  )}
                >
                  {item.icon}
                </span>
                <span className="flex-1">{item.label}</span>
              </NavLink>
            )}
          </div>
        ))}
      </nav>

      {/* User info & Logout — fixed footer */}
      <div className="flex-shrink-0 border-t border-sidebar-border p-4">
        {user && (
          <div className="flex items-center gap-3 mb-3">
            <Avatar name={`${user.firstName} ${user.lastName}`} size="sm" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {user.firstName} {user.lastName}
              </p>
              <p className="text-xs text-sidebar-text truncate">{user.email}</p>
              {user.role && (
                <p className="text-xs text-primary-400 capitalize">{user.role}</p>
              )}
            </div>
          </div>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={onLogout}
          className="w-full justify-start text-sidebar-text hover:text-white hover:bg-sidebar-hover transition-all duration-150"
        >
          <LogOut className="h-4 w-4 mr-2" />
          {logoutLabel}
        </Button>
        {footerExtras}
      </div>

      {children}
    </aside>
  );
}
